import React, { useState } from 'react';
import { Trash2, Lock, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
type DeleteStep = 'confirm' | 'reauth-password' | 'reauth-google' | 'deleting' | 'success' | 'reauth-error' | 'delete-error';
interface DeleteAccountModalProps {
  onClose: () => void;
  onDeleted: () => void;
  authMethod?: 'email' | 'google';
  initialStep?: DeleteStep;
}
export function DeleteAccountModal({
  onClose,
  onDeleted,
  authMethod = 'email',
  initialStep = 'confirm'
}: DeleteAccountModalProps) {
  const [step, setStep] = useState<DeleteStep>(initialStep);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const handleConfirmDelete = () => {
    if (authMethod === 'google') {
      setStep('reauth-google');
    } else {
      setStep('reauth-password');
    }
  };
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setPasswordError('Enter your password to continue');
      return;
    }
    setPasswordError('');
    setStep('deleting');
    // Simulate deletion
    setTimeout(() => {
      setStep('success');
    }, 1500);
  };
  const handleGoogleReauth = () => {
    setStep('deleting');
    // Simulate deletion
    setTimeout(() => {
      setStep('success');
    }, 1500);
  };
  const handleSuccessDone = () => {
    onDeleted();
  };
  // Force step for debug
  const forceStep = (s: DeleteStep) => {
    setStep(s);
    setPassword('');
    setPasswordError('');
  };
  return <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-155">
      <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden" data-id="element-156">
        {/* STEP: Confirmation */}
        {step === 'confirm' && <div className="p-6" data-id="element-157">
            <div className="flex justify-end mb-2" data-id="element-158">
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-159">
                <X size={20} data-id="element-160" />
              </button>
            </div>

            <div className="w-14 h-14 bg-pink-100 text-pink-500 rounded-full flex items-center justify-center mb-4 mx-auto" data-id="element-161">
              <Trash2 size={28} data-id="element-162" />
            </div>

            <h3 className="text-xl font-extrabold text-slate-800 mb-3 text-center" data-id="element-163">
              Delete account?
            </h3>

            <p className="text-sm font-medium text-slate-600 leading-relaxed mb-3 text-center" data-id="element-164">
              This will permanently remove your GymBuddy account and your app
              data. This action cannot be undone.
            </p>

            <div className="bg-pink-50 rounded-2xl p-3 mb-6" data-id="element-165">
              <p className="text-xs font-medium text-pink-700 leading-relaxed text-center" data-id="element-166">
                You'll lose access to your workout history, analytics, active
                program, and saved settings.
              </p>
            </div>

            <div className="space-y-3" data-id="element-167">
              <Button variant="secondary" fullWidth onClick={handleConfirmDelete} data-id="element-168">
                Delete my account
              </Button>
              <Button variant="ghost" fullWidth onClick={onClose} data-id="element-169">
                Cancel
              </Button>
            </div>
          </div>}

        {/* STEP: Re-auth with Password */}
        {(step === 'reauth-password' || step === 'reauth-error') && <div className="p-6" data-id="element-170">
            <div className="flex justify-end mb-2" data-id="element-171">
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-172">
                <X size={20} data-id="element-173" />
              </button>
            </div>

            <div className="w-14 h-14 bg-slate-100 text-slate-500 rounded-full flex items-center justify-center mb-4 mx-auto" data-id="element-174">
              <Lock size={28} data-id="element-175" />
            </div>

            <h3 className="text-xl font-extrabold text-slate-800 mb-2 text-center" data-id="element-176">
              Verify your account
            </h3>

            <p className="text-sm font-medium text-slate-500 leading-relaxed mb-2 text-center" data-id="element-177">
              Enter your password to continue with account deletion.
            </p>
            <p className="text-xs font-medium text-slate-400 leading-relaxed mb-5 text-center" data-id="element-178">
              flex@gymbuddy.app
            </p>

            {step === 'reauth-error' && <div className="flex items-center gap-2 bg-pink-50 border border-pink-100 rounded-2xl p-3 mb-4 animate-in fade-in duration-200" data-id="element-179">
                <AlertCircle size={16} className="text-pink-500 shrink-0" data-id="element-180" />
                <p className="text-xs font-medium text-pink-700" data-id="element-181">
                  Please verify your account to continue
                </p>
              </div>}

            <form onSubmit={handlePasswordSubmit} className="space-y-5" data-id="element-182">
              <Input label="Password" icon={Lock} type="password" placeholder="Enter your password" value={password} onChange={e => {
            setPassword(e.target.value);
            setPasswordError('');
          }} error={passwordError} data-id="element-183" />

              <div className="space-y-3" data-id="element-184">
                <Button type="submit" variant="secondary" fullWidth data-id="element-185">
                  Continue
                </Button>
                <Button variant="ghost" fullWidth onClick={onClose} data-id="element-186">
                  Cancel
                </Button>
              </div>
            </form>
          </div>}

        {/* STEP: Re-auth with Google */}
        {step === 'reauth-google' && <div className="p-6" data-id="element-187">
            <div className="flex justify-end mb-2" data-id="element-188">
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-189">
                <X size={20} data-id="element-190" />
              </button>
            </div>

            <div className="w-14 h-14 bg-slate-100 text-slate-500 rounded-full flex items-center justify-center mb-4 mx-auto" data-id="element-191">
              <Lock size={28} data-id="element-192" />
            </div>

            <h3 className="text-xl font-extrabold text-slate-800 mb-2 text-center" data-id="element-193">
              Verify your account
            </h3>

            <p className="text-sm font-medium text-slate-500 leading-relaxed mb-5 text-center" data-id="element-194">
              Sign in with Google to continue with account deletion.
            </p>

            <div className="space-y-3" data-id="element-195">
              <Button variant="google" fullWidth onClick={handleGoogleReauth} data-id="element-196">
                Continue with Google
              </Button>
              <Button variant="ghost" fullWidth onClick={onClose} data-id="element-197">
                Cancel
              </Button>
            </div>
          </div>}

        {/* STEP: Deleting (loading) */}
        {step === 'deleting' && <div className="p-6 py-12" data-id="element-198">
            <div className="flex flex-col items-center" data-id="element-199">
              <div className="w-14 h-14 bg-pink-100 text-pink-500 rounded-full flex items-center justify-center mb-4 animate-pulse" data-id="element-200">
                <Trash2 size={28} data-id="element-201" />
              </div>
              <p className="text-sm font-bold text-slate-600" data-id="element-202">
                Deleting your account...
              </p>
            </div>
          </div>}

        {/* STEP: Success */}
        {step === 'success' && <div className="p-6 py-10" data-id="element-203">
            <div className="w-14 h-14 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mb-4 mx-auto animate-in zoom-in-50 duration-300" data-id="element-204">
              <CheckCircle2 size={28} data-id="element-205" />
            </div>

            <h3 className="text-xl font-extrabold text-slate-800 mb-2 text-center" data-id="element-206">
              Your account has been deleted
            </h3>

            <p className="text-sm font-medium text-slate-500 leading-relaxed mb-6 text-center" data-id="element-207">
              Your account and app data have been removed.
            </p>

            <Button variant="primary" fullWidth onClick={handleSuccessDone} data-id="element-208">
              Done
            </Button>
          </div>}

        {/* STEP: Deletion Error */}
        {step === 'delete-error' && <div className="p-6" data-id="element-209">
            <div className="flex justify-end mb-2" data-id="element-210">
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-211">
                <X size={20} data-id="element-212" />
              </button>
            </div>

            <div className="w-14 h-14 bg-amber-100 text-amber-500 rounded-full flex items-center justify-center mb-4 mx-auto" data-id="element-213">
              <AlertCircle size={28} data-id="element-214" />
            </div>

            <h3 className="text-xl font-extrabold text-slate-800 mb-3 text-center" data-id="element-215">
              Something went wrong
            </h3>

            <p className="text-sm font-medium text-slate-600 leading-relaxed mb-6 text-center" data-id="element-216">
              We couldn't delete your account right now. Please try again.
            </p>

            <div className="space-y-3" data-id="element-217">
              <Button variant="secondary" fullWidth onClick={() => setStep('confirm')} data-id="element-218">
                Try again
              </Button>
              <Button variant="ghost" fullWidth onClick={onClose} data-id="element-219">
                Cancel
              </Button>
            </div>
          </div>}
      </div>
    </div>;
}